// Magic-link landing page. Customer clicks the link from their invite email,
// arrives here with ?token=…, sets a password, and gets logged in.
// This page lives OUTSIDE the portal layout (no slug context) so it works
// regardless of which portal's invite they were sent.
import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, Lifebuoy } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function CustomerSetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (password.length < 8) return toast.error('Password must be at least 8 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/customer/set-password`, { token, password }, { withCredentials: true });
      setDone(true);
      toast.success('Password set');
      // Discover this customer's portal slug so we can drop them right
      // into the portal they were invited to. Falls back to a static
      // success card after 5s if the lookup misses.
      try {
        const portalsRes = await axios.get(`${API}/public/portals-for-org?org_id=${encodeURIComponent(data.org_id)}`).catch(() => null);
        const slug = portalsRes?.data?.slug;
        if (slug) {
          setTimeout(() => navigate(`/help/${slug}/requests`), 1200);
        }
      } catch (_) { /* show static success card */ }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-50 to-zinc-100 px-4">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl shadow-sm p-8">
        <div className="flex items-center gap-2 text-blue-600 mb-2">
          <Lifebuoy size={18} weight="fill" /><span className="text-xs font-semibold uppercase tracking-wider">Service Desk</span>
        </div>
        {done ? (
          <div className="text-center py-4">
            <CheckCircle size={36} weight="fill" className="text-emerald-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-zinc-900 font-['Outfit']">Password set</h1>
            <p className="text-sm text-zinc-500 mt-2">You're signed in. Head to the portal to raise or track requests.</p>
            <p className="text-xs text-zinc-400 mt-3">
              Tip: Bookmark the help-portal URL your admin shared.
            </p>
          </div>
        ) : !token ? (
          <p className="text-sm text-zinc-500">Invalid invite link. Please ask your admin to resend.</p>
        ) : (
          <form onSubmit={submit}>
            <h1 className="text-xl font-bold text-zinc-900 font-['Outfit']">Set your password</h1>
            <p className="text-sm text-zinc-500 mt-1">Pick a password to access your help portal.</p>
            <div className="space-y-3 mt-5">
              <div>
                <Label className="text-xs">Password</Label>
                <Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)}
                  placeholder="At least 8 characters" data-testid="customer-password-input" />
              </div>
              <div>
                <Label className="text-xs">Confirm password</Label>
                <Input type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)}
                  data-testid="customer-password-confirm" />
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="customer-password-submit">
                {submitting ? 'Setting password…' : 'Set password & continue'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
